import * as React from 'react';
import { Overlay } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import ImageBase from 'webapp/components/image';
import { Button } from 'webapp/components/button';
import { Theme } from 'webapp/theme';
import { Discard } from 'webapp/components/icons';
import { ZIndex } from 'webapp/z-index';
import { Carousel, useCarousel } from 'webapp/components/Carousel';
import { createMaxSmallViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { scrollbarDecorator } from 'webapp/decorators/scrollbar';

type Props = {
  images: Excite.ContentApi.ImageApiModel[];
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  startSlide?: number;
};

export const ReviewImageFullscreenGallery = ({ images = [], isOpen = false, setIsOpen, startSlide = 0 }: Props) => {
  const carousel = useCarousel(undefined, { startIndex: startSlide, align: 'center' });
  const [zoomedIn, setZoomedIn] = React.useState(false);

  return (
    <Overlay zIndex={ZIndex.Overlay} isEnabled={isOpen} onClose={() => setIsOpen(false)}>
      <ImageCarousel
        containerDecorator={containerDecorator}
        buttonDecorator={buttonDecorator}
        arrowNavigation={images.length > 0}
        {...carousel}
      >
        {images.map((image, index) => (
          <Slide key={index}>
            <Image image={image} css={zoomedIn && zoomedInDecorator} onClick={() => setZoomedIn(!zoomedIn)} />
          </Slide>
        ))}
      </ImageCarousel>
      <CloseButton square disableRippleEffect onClick={() => setIsOpen(false)} as={Theme.Transparent}>
        <Discard />
      </CloseButton>
    </Overlay>
  );
};

const containerDecorator = styled({ height: 'calc(100vh - 100px)' });
const buttonDecorator = styled(createMinLargeViewportDecorator({ width: 48, height: 48, top: 'calc(50% - 24px)' }));

const zoomedInDecorator = styled(
  createMinLargeViewportDecorator({
    width: '100%',
    height: 'auto',
    cursor: 'zoom-out',
  }),
);

const Slide = styled.div(
  scrollbarDecorator,
  createMaxSmallViewportDecorator({
    height: '100%',
  }),
  {
    height: 'calc(100vh - 100px)',
    width: 'auto',
    objectFit: 'contain',
    overflowY: 'auto',
  },
);

const IconButton = styled(Button, {
  position: 'absolute',
  fontSize: t => t.fontSize.xl,
  color: 'white',
  ':disabled': {
    backgroundColor: 'transparent',
    color: 'black',
  },
});
const CloseButton = styled(
  IconButton,
  createMinLargeViewportDecorator({
    top: 10,
    right: 10,
  }),
  {
    top: 0,
    right: 0,
  },
);

const ImageCarousel = styled(Carousel, {
  flexGrow: 1,
  height: 'calc(100vh - 100px)',
  padding: {
    y: t => t.sharedSpacing.l,
  },
});

const Image = styled(ImageBase, {
  height: '100%',
  objectFit: 'contain',
  display: 'block',
  margin: { x: 'auto' },
  cursor: 'zoom-in',
});
